import React, { useEffect, useState } from "react";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import IconArrowUp from "@/app/(components)/generics/Icons/IconArrowUp";
import inputStyles from "@/app/(components)/generics/inputs/default/Input/Input.module.css";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import Popover from "@/app/(components)/generics/Popover/Popover";
import PopoverContent from "@/app/(components)/generics/Popover/PopoverContent";
import PopoverTrigger from "@/app/(components)/generics/Popover/PopoverTrigger";
import { TKey } from "@/app/translations/TKey";
import { T } from "@/app/translations/Translations";
import { UStyles } from "@/app/utils/UStyles";
import defaultInputStyles from "../../styles/DefaultInput.module.css";
import styles from "./SelectInput.module.css";

type Props = {
  data: any;
  dataKey?: string;
  options: { [key: string]: string } | [string, string][];
  defaultOption?: number | string;
  withEmptyOption?: boolean;
  withSearchBar?: {
    lang: string;
  };
  textCenter?: boolean;
  lang?: string;
  label?: string;
  labelSuffix?: React.ReactNode;
  required?: boolean;
  showRequiredLabel?: boolean;
  errorMessage?: string;
  onChange?: (data: any) => void;
};

export function SelectInput(props: Props) {
  const {
    data,
    dataKey = "",
    options,
    defaultOption = undefined,
    withEmptyOption = false,
    withSearchBar,
    textCenter,
    lang,
    label = "",
    labelSuffix,
    required,
    showRequiredLabel,
    errorMessage = "",
    onChange = async () => {},
  } = props;

  const [optionKey, setOptionKey] = useState<string>(
    defaultOption ? defaultOption : dataKey ? data[dataKey] : Object.values(data)[0],
  );

  const [popoverVisible, setPopoverVisible] = useState<boolean>(false);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredOptions, setFilteredOptions] = useState<[string, string][]>([]);

  useEffect(() => {
    setOptionKey(defaultOption ? defaultOption : dataKey ? data[dataKey] : Object.values(data)[0]);
  }, [defaultOption, dataKey, data]);

  useEffect(() => {
    const optionsEntries = Array.isArray(options) ? options : Object.entries(options);

    const filtered = optionsEntries.filter(([_optionKey, optionValue]) => {
      return String(optionValue).toLowerCase().includes(searchTerm.toLowerCase());
    });

    if (withEmptyOption) {
      filtered.unshift(["", " "]);
    }

    setFilteredOptions(filtered);
  }, [searchTerm, options, withEmptyOption]);

  const handleOptionClick = async (selectedKey: string) => {
    data[dataKey] = selectedKey;

    setOptionKey(selectedKey);

    if (onChange) {
      await onChange(data);
    }

    setPopoverVisible(false);
  };

  return (
    <div className={styles.container}>
      {label && (
        <label className={inputStyles.label}>
          {label}
          {showRequiredLabel &&
            (required ? (
              <span className={inputStyles.labelRequired}>*</span>
            ) : (
              <span className={inputStyles.labelOptional}>({T(TKey.OPTIONAL, lang)})</span>
            ))}
          {labelSuffix && <span className={inputStyles.labelSuffix}>{labelSuffix}</span>}
        </label>
      )}

      <Popover
        isVisible={popoverVisible}
        setIsVisible={setPopoverVisible}
        openAfterClick
        closeWhenClickOutside
        className={styles.popoverContainer}
      >
        <PopoverTrigger>
          <div className={styles.trigger}>
            <span>
              {Array.isArray(options)
                ? options.find((o) => o[0] === optionKey)?.[1] || ""
                : options[optionKey]}
            </span>
            {popoverVisible ? <IconArrowUp /> : <IconArrowDown />}
          </div>
        </PopoverTrigger>

        <PopoverContent
          position={{
            default: {
              top: "55px",
            },
          }}
        >
          <div className={styles.select}>
            {withSearchBar?.lang && (
              <input
                type="text"
                className={styles.searchBar}
                placeholder={"Rechercher..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}

            <div className={styles.options}>
              {withSearchBar && Object.keys(filteredOptions).length === 0 ? (
                <p className={styles.notFoundText}>Pas de résultats...</p>
              ) : (
                filteredOptions.map(([optKey, optValue]) => (
                  <button
                    key={optKey}
                    type="button"
                    className={`${styles.option} ${optionKey === optKey ? styles.optionSelect : ""}`}
                    onClick={() => handleOptionClick(optKey)}
                    style={{
                      textAlign: textCenter ? "center" : "left",
                    }}
                  >
                    {optValue}
                  </button>
                ))
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {errorMessage && (
        <div className={UStyles.classNames(styles.errorMessage, defaultInputStyles.errorMessage)}>
          <ErrorMessage message={errorMessage} />
        </div>
      )}
    </div>
  );
}
